import _ from "@/boot/lodash";
import store from "@/store";
import router from "@/router";
import i18n from "@/i18n";
import { SnackbarProgrammatic as $snackbar } from "buefy";
import { ToastProgrammatic as $toast } from "buefy";
import { Contexts } from "@upmind-automation/types";
import { AdminRoutes } from "@/data/enums/router";
import type { NavigationGuardNext, Route } from "vue-router";
import type { CreateElement } from "vue";

const guardAdminAuthRoutes = async (
  to: Route,
  from: Route,
  next: NavigationGuardNext
) => {
  const allowAuthenticated = _.get(to.meta, "allowAuthenticated", false);
  if (store.getters["auth/admin/isAuthenticated"] && !allowAuthenticated) {
    store.dispatch("ui/endRouting");
    return next(
      to.query.redirect
        ? {
            // Replace '\\' from start to prevent attack vector
            path: (to.query.redirect as string).replace(/^\\+/, ""),
            replace: true
          }
        : !from.matched.length
        ? { name: AdminRoutes.DASHBOARD, replace: true }
        : false
    );
  }
  store.commit("user", {});
  next();
};

export default [
  {
    path: "/admin/auth/preferences",
    component: () => import("@/skeleton/admin/loggedOut/minimalLayout.vue"),
    beforeEnter: guardAdminAuthRoutes,
    children: [
      {
        path: "",
        alias: "/admin/preferences",
        component: () => import("@/views/admin/notifications/index.vue"),
        meta: {
          title: "_.notification_preferences",
          allowAuthenticated: false
        }
      }
    ]
  },
  {
    path: "/admin/auth",
    redirect: "/admin/login",
    component: () => import("@/skeleton/admin/loggedOut/index.vue"),
    beforeEnter: guardAdminAuthRoutes,
    children: [
      {
        path: "login",
        alias: "/admin/login",
        name: AdminRoutes.LOGIN,
        component: () => import("@/views/admin/auth/login/index.vue"),
        meta: {
          title: "_.login",
          allowAuthenticated: false
        }
      },
      {
        path: "forgotten-password",
        alias: "/admin/forgotten-password",
        name: "adminForgottenPassword",
        component: () =>
          import("@/views/admin/auth/forgottenPassword/index.vue"),
        meta: {
          title: "_.recover_account",
          allowAuthenticated: false
        }
      },
      {
        path: "reset-password",
        name: "resetPassword",
        alias: "/admin/reset-password",
        component: () => import("@/views/admin/auth/resetPassword/index.vue"),
        meta: {
          title: "_.reset_password",
          allowAuthenticated: true
        }
      },
      {
        path: "verify",
        alias: "/admin/verify",
        component: () => import("@/views/admin/auth/verify/index.vue"),
        meta: {
          title: "_.verify_email",
          allowAuthenticated: false
        }
      }
    ]
  },
  {
    path: "/admin/logout",
    name: AdminRoutes.LOGOUT,
    beforeEnter: async (to: Route, from: Route, next: NavigationGuardNext) => {
      /** Immediately bypass to show loader (do not await) */
      next();
      /** First, we end the existing user session */
      await store.dispatch("auth/logout", Contexts.ADMIN);
      /** Next, we get current brand again in case the staff user was operating
      in a brand different to that of the current oauth client (possible with
      multi-brand orgs and impersonation) */
      await store.dispatch("brand/get", { ignoreStored: true });
      /** Next, we attempt to restore any backup (admin) token */
      if (await store.dispatch("auth/restoreToken", Contexts.ADMIN)) {
        return Promise.all([
          router.replace({ name: "adminUsers" }),
          $snackbar.open({
            duration: 2500,
            actionText: i18n.t("_action.dismiss") as string,
            message: i18n.t("_sentence.auth.admin_session_restored") as string,
            type: "is-info"
          })
        ]);
      }
      /** Otherwise... */
      /** We relay any specified toast message */
      if (from.matched.length) {
        const message = _.get(to.params, "toast");
        if (message) $toast.open({ message });
      }
      /** We record a redirect path to pass on */
      const query: Record<string, string> = {};
      if (!!from?.name && !from?.fullPath?.startsWith("/admin/logout")) {
        query["redirect"] = from.fullPath;
      }
      /** And finally, we route to `/admin/login` */
      return router
        .replace({ name: AdminRoutes.LOGIN, query })
        .catch(err => err);
    },
    component: {
      name: "AdminLogout",
      render(h: CreateElement) {
        return h("loading", {
          props: {
            isAbsolute: true
          }
        });
      }
    }
  },
  {
    path: "/admin/login-as",
    name: AdminRoutes.LOGIN_AS,
    component: {
      name: "AdminLoginAs",
      created() {
        return router
          .replace({ name: AdminRoutes.DASHBOARD })
          .catch(err => err);
      },
      render(h) {
        return h("loading", {
          props: {
            isAbsolute: true
          }
        });
      }
    }
  }
];
